import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Loader2, Mail, Phone, Calendar } from 'lucide-react';
import { adminService } from '../../services/api';
import { useToast } from '../../hooks/use-toast';

const AdminContacts = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const { toast } = useToast();

  useEffect(() => {
    fetchContacts();
  }, [filter]);

  const fetchContacts = async () => {
    try {
      const statusParam = filter === 'all' ? undefined : filter;
      const response = await adminService.contacts.getAll(statusParam);
      setContacts(response.data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast({
        title: '錯誤',
        description: '無法載入聯絡表單',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await adminService.contacts.updateStatus(id, status);
      toast({ title: '成功', description: '狀態已更新' });
      fetchContacts();
    } catch (error) {
      console.error('Error updating status:', error);
      toast({
        title: '錯誤',
        description: '更新失敗',
        variant: 'destructive'
      });
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: { bg: '#c8ff00', text: 'black', label: '新查詢' },
      contacted: { bg: '#00b6a5', text: 'white', label: '已聯絡' },
      resolved: { bg: '#gray', text: 'white', label: '已完成' }
    };
    const style = styles[status] || styles.new;
    return (
      <Badge style={{ backgroundColor: style.bg, color: style.text }}>
        {style.label}
      </Badge>
    );
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold" style={{ color: '#00b6a5' }}>聯絡表單</h1>
        <p className="text-gray-600 mt-2">查看及管理客戶查詢</p>
      </div>

      {/* Filters */}
      <div className="mb-6 flex gap-2">
        <Button
          variant={filter === 'all' ? 'default' : 'outline'}
          onClick={() => setFilter('all')}
          style={{ backgroundColor: filter === 'all' ? '#00b6a5' : 'transparent' }}
          className={filter === 'all' ? 'text-white' : ''}
        >
          全部
        </Button>
        <Button
          variant={filter === 'new' ? 'default' : 'outline'}
          onClick={() => setFilter('new')}
          style={{ backgroundColor: filter === 'new' ? '#00b6a5' : 'transparent' }}
          className={filter === 'new' ? 'text-white' : ''}
        >
          新查詢
        </Button>
        <Button
          variant={filter === 'contacted' ? 'default' : 'outline'}
          onClick={() => setFilter('contacted')}
          style={{ backgroundColor: filter === 'contacted' ? '#00b6a5' : 'transparent' }}
          className={filter === 'contacted' ? 'text-white' : ''}
        >
          已聯絡
        </Button>
        <Button
          variant={filter === 'resolved' ? 'default' : 'outline'}
          onClick={() => setFilter('resolved')}
          style={{ backgroundColor: filter === 'resolved' ? '#00b6a5' : 'transparent' }}
          className={filter === 'resolved' ? 'text-white' : ''}
        >
          已完成
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-12 w-12 animate-spin" style={{ color: '#00b6a5' }} />
        </div>
      ) : contacts.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-gray-500">
            沒有聯絡表單
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {contacts.map((contact) => (
            <Card key={contact.id}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-lg">{contact.name}</CardTitle>
                    <p className="text-sm text-gray-600 mt-1">{contact.subject}</p>
                  </div>
                  {getStatusBadge(contact.status)}
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div className="flex items-center space-x-2">
                    <Phone className="h-4 w-4 text-gray-400" />
                    <span className="text-sm">{contact.phone}</span>
                  </div>
                  {contact.email && (
                    <div className="flex items-center space-x-2">
                      <Mail className="h-4 w-4 text-gray-400" />
                      <span className="text-sm">{contact.email}</span>
                    </div>
                  )}
                  <div className="flex items-center space-x-2">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <span className="text-sm">
                      {new Date(contact.createdAt).toLocaleString('zh-HK')}
                    </span>
                  </div>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg mb-4">
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{contact.message}</p>
                </div>
                <div className="flex gap-2">
                  {contact.status === 'new' && (
                    <Button
                      size="sm"
                      style={{ backgroundColor: '#00b6a5' }}
                      className="text-white"
                      onClick={() => updateStatus(contact.id, 'contacted')}
                    >
                      標記為已聯絡
                    </Button>
                  )}
                  {contact.status === 'contacted' && (
                    <Button
                      size="sm"
                      style={{ backgroundColor: '#00b6a5' }}
                      className="text-white"
                      onClick={() => updateStatus(contact.id, 'resolved')}
                    >
                      標記為已完成
                    </Button>
                  )}
                  <a href={`https://api.whatsapp.com/send?phone=${contact.phone.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer">
                    <Button size="sm" variant="outline">
                      WhatsApp 聯絡
                    </Button>
                  </a>
                  <a href={`tel:${contact.phone}`}>
                    <Button size="sm" variant="outline">
                      致電
                    </Button>
                  </a>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminContacts;
